import React, { useState, useMemo, useEffect, useCallback } from "react";
import { format } from "date-fns";
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";

import { ContainerWrapper, TableWrapperStyles } from "./styled";
import { CommonDashboardWrapper } from "styles/GlobalStyles";
import {
  CardFlexWrapperStyles,
  CardWrapperStyles
} from "pages/TalentUploadRequest/styled";
import { infoOptions, createdDateMenuItems, searchFieldOptions } from "./data";

import Icon from "components/Icon/Icon";
import RightIconOutlineRegularIcon from "assets/SVGComponents/RightIconOutlineRegularIcon";
import {
  MagnifyingGlassOutlineRegularIcon,
  XOutlineRegularIcon
} from "assets/SVGComponents";
import { DotIcon, XCircleIcon } from "@phosphor-icons/react";

import PillButtons from "components/Buttons/PillButtons";
import Input from "components/Input";
import CommonTable from "components/Tables/CommonTable";
import columns from "./columns";
import { formatCustomRange, getLastNDaysRange } from "utils/helper";

import { useUsersSlice } from "./slice/index";
import {
  getAllMarketUsers,
  selectCompanyUsersByStatus
} from "./slice/selectors";
import { useDispatch, useSelector } from "react-redux";
import CustomDateModal from "components/CustomDateModal/index";
import Select from "components/Select/index";
import UserStatusModal from "../../components/Modal/UserStatus";

const UsersManagement = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { actions } = useUsersSlice();

  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get("page")) || 1;

  const {
    loading,
    users,
    statusMeta,
    pagination,
    statusDropdownCounts,
    domainUserCounts
  } = useSelector(getAllMarketUsers);

  const { loading: userStatusLoading, users: userStatus } = useSelector(
    selectCompanyUsersByStatus
  );

  const [isInitialized, setIsInitialized] = useState(false);

  const [userFilters, setUserFilters] = useState({
    status: {},
    createdAt: {},
    phoneVerifiedAt: {},
    emailVerifiedAt: {},
    search: "",
    search_field: "phone_number"
  });

  const [sortConfig, setSortConfig] = useState({
    sort_by: "",
    sort_order: ""
  });

  // Track whether sort was explicitly triggered by the user
  const [isSortUserTriggered, setIsSortUserTriggered] = useState(false);

  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [customDateRange, setCustomDateRange] = useState([null, null]);
  const [showDateModal, setShowDateModal] = useState(false);
  const [activeFilterType, setActiveFilterType] = useState(null);

  const [showCompanyUsersModal, setShowCompanyUsersModal] = useState(false);
  const [companyUserStatus, setCompanyUserStatus] = useState({
    companyId: null,
    status: null
  });

  const domainCountMap = useMemo(() => {
    if (!domainUserCounts || typeof domainUserCounts !== "object") {
      return {};
    }

    return domainUserCounts;
  }, [domainUserCounts]);

  const handleStatusCountClick = useCallback(
    ({ companyId, status }) => {
      setCompanyUserStatus({
        companyId,
        status
      });

      setShowCompanyUsersModal(true);

      dispatch(
        actions.fetchCompanyUsersByStatus({
          company_id: [companyId],
          status
        })
      );
    },
    [dispatch, actions]
  );

  const handleCloseCompanyUsersModal = useCallback(() => {
    setShowCompanyUsersModal(false);

    setCompanyUserStatus({
      companyId: null,
      status: null
    });
  }, []);

  const tableColumns = useMemo(
    () =>
      columns(navigate, searchParams, domainCountMap, handleStatusCountClick),
    [navigate, searchParams, domainCountMap, handleStatusCountClick]
  );

  /* ---------------------------
     Read Filters from URL
     (sort is NOT read from URL — only written when user triggers it)
  ----------------------------*/
  useEffect(() => {
    const search = searchParams.get("search") || "";
    const status = searchParams.get("status") || "";

    const createdFrom = searchParams.get("createdFrom");
    const createdTo = searchParams.get("createdTo");

    const phoneFrom = searchParams.get("phoneFrom");
    const phoneTo = searchParams.get("phoneTo");

    const emailFrom = searchParams.get("emailFrom");
    const emailTo = searchParams.get("emailTo");

    const searchFieldParam = searchParams.get("search_field") || "phone_number";

    const sortByParam = searchParams.get("sort_by");
    const sortOrderParam = searchParams.get("sort_order");

    if (sortByParam && sortOrderParam) {
      setSortConfig({
        sort_by: sortByParam,
        sort_order: sortOrderParam
      });
      setIsSortUserTriggered(true);
    }

    setSearchTerm(search);
    setDebouncedSearch(search);

    setUserFilters({
      status: status ? { value: status, label: status } : {},
      search_field: searchFieldParam,
      createdAt:
        createdFrom && createdTo
          ? {
              label: `${createdFrom} - ${createdTo}`,
              fromDate: createdFrom,
              toDate: createdTo
            }
          : {},
      phoneVerifiedAt:
        phoneFrom && phoneTo
          ? {
              label: `${phoneFrom} - ${phoneTo}`,
              fromDate: phoneFrom,
              toDate: phoneTo
            }
          : {},
      emailVerifiedAt:
        emailFrom && emailTo
          ? {
              label: `${emailFrom} - ${emailTo}`,
              fromDate: emailFrom,
              toDate: emailTo
            }
          : {}
    });

    setIsInitialized(true);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(searchParams.toString());

    if (params.get("mode")) {
      params.delete("mode");
      setSearchParams(params);
    }
  }, []);

  /* ---------------------------
     Update URL when filters change
  ----------------------------*/
  useEffect(() => {
    if (!isInitialized) return;

    const params = new URLSearchParams(searchParams.toString());

    const currentSearch = searchParams.get("search") || "";

    // Reset page only when search changes
    if (debouncedSearch !== currentSearch) {
      params.set("page", 1);
    }

    if (debouncedSearch) {
      params.set("search", debouncedSearch);
    } else {
      params.delete("search");
    }

    if (userFilters.status?.value) {
      params.set("status", userFilters.status.value);
    } else {
      params.delete("status");
    }

    if (userFilters.search_field) {
      params.set("search_field", userFilters.search_field);
    }

    if (isSortUserTriggered && sortConfig.sort_by) {
      params.set("sort_by", sortConfig.sort_by);
      params.set("sort_order", sortConfig.sort_order);
    }

    setSearchParams(params);
  }, [userFilters, debouncedSearch, isInitialized, sortConfig]);

  /* ---------------------------
     Fetch Users
  ----------------------------*/
  useEffect(() => {
    if (!isInitialized) return;
    handleUserFetch(page);
  }, [userFilters, debouncedSearch, isInitialized, page]);

  const addDateFilter = (filter, key) => {
    if (filter?.label && filter.label !== "Lifetime") {
      return { [key]: filter };
    }
    return {};
  };

  const handleUserFetch = (page = 1, sortBy, sortOrder) => {
    const payload = {
      status: userFilters.status?.value || "",
      search: debouncedSearch || "",
      search_field: userFilters.search_field || "phone_number",
      page,
      limit: 10,
      ...addDateFilter(userFilters.createdAt, "createdAt"),
      ...addDateFilter(userFilters.phoneVerifiedAt, "phoneVerifiedAt"),
      ...addDateFilter(userFilters.emailVerifiedAt, "emailVerifiedAt")
    };

    if (isSortUserTriggered) {
      payload.sort_by = sortBy || sortConfig.sort_by;
      payload.sort_order = sortOrder || sortConfig.sort_order;
    }

    dispatch(actions.fetchAllUsers(payload));
  };

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams.toString());
    params.set("page", newPage);
    setSearchParams(params);
  };

  const handleSortChange = (columnId) => {
    setIsSortUserTriggered(true);

    setSortConfig((prev) => {
      const isSameColumn = prev.sort_by === columnId;

      const newOrder =
        isSameColumn && prev.sort_order === "asc" ? "desc" : "asc";

      const newConfig = {
        sort_by: columnId,
        sort_order: newOrder
      };

      dispatch(
        actions.fetchAllUsers({
          status: userFilters.status?.value || "",
          search: debouncedSearch || "",
          search_field: userFilters.search_field || "phone_number",
          page: 1,
          limit: 10,
          sort_by: newConfig.sort_by,
          sort_order: newConfig.sort_order
        })
      );

      return newConfig;
    });
  };
  /* ---------------------------
     Debounce Search
  ----------------------------*/
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 300);

    return () => clearTimeout(handler);
  }, [searchTerm]);

  const statusMenuItems = infoOptions.map((option) => ({
    label: option.label,
    value: option.apiKey,
    count: statusDropdownCounts[option.apiKey] || 0
  }));

  const updateCustomDateRange = (range) => {
    setCustomDateRange(range);

    if (activeFilterType) {
      setUserFilters({
        ...userFilters,
        [activeFilterType]: {
          label: formatCustomRange(range),
          fromDate: format(range[0], "yyyy-MM-dd"),
          toDate: format(range[1], "yyyy-MM-dd")
        }
      });
    }

    setShowDateModal(false);
    setActiveFilterType(null);
  };

  const updateDateFilter = (filterType, item) => {
    if (item.value === "custom") {
      setCustomDateRange([null, null]);
      setActiveFilterType(filterType);
      setShowDateModal(true);
      return;
    }

    const range = getLastNDaysRange(item.value);

    setUserFilters({
      ...userFilters,
      [filterType]: {
        label: item.label,
        fromDate: range.fromDate,
        toDate: range.toDate
      }
    });
  };

  return (
    <ContainerWrapper>
      <CommonDashboardWrapper>
        <div className="common-dashboard-wrapper">
          <h1 className="common-heading">All Users (Brands)</h1>
        </div>

        <CardFlexWrapperStyles>
          {infoOptions.map((option) => (
            <CardWrapperStyles key={option.label}>
              <div className="card-list">
                <Icon
                  color={option.color}
                  component={DotIcon}
                  weight="fill"
                  format="outline"
                />
                <span className="card-title">{option.label}</span>
              </div>

              <p className="card-count">{statusMeta[option.apiKey]}</p>
            </CardWrapperStyles>
          ))}
        </CardFlexWrapperStyles>

        <TableWrapperStyles>
          <div className="filter-bar">
            <div className="filter-bar-menus">
              <PillButtons
                placeholderText="Account Status"
                statusPlaceholder="statusActive"
                mainLabelText={userFilters.status.label}
                rightIcon={<RightIconOutlineRegularIcon />}
                onSelect={(item) =>
                  setUserFilters({ ...userFilters, status: item })
                }
                menuItems={statusMenuItems}
                size="M"
                maxWidth="max-content"
                customClassName="status-width"
                activeRightIcon={
                  <Icon
                    component={XCircleIcon}
                    format="outline"
                    weight="regular"
                  />
                }
              />

              <PillButtons
                placeholderText="User created at"
                statusPlaceholder="statusActive"
                mainLabelText={userFilters.createdAt.label}
                rightIcon={<RightIconOutlineRegularIcon />}
                onSelect={(item) => updateDateFilter("createdAt", item)}
                menuItems={createdDateMenuItems}
                size="M"
                maxWidth="max-content"
                customClassName="status-width"
              />

              <PillButtons
                placeholderText="Phone Verified at"
                statusPlaceholder="statusActive"
                mainLabelText={userFilters.phoneVerifiedAt.label}
                rightIcon={<RightIconOutlineRegularIcon />}
                onSelect={(item) => updateDateFilter("phoneVerifiedAt", item)}
                menuItems={createdDateMenuItems}
                size="M"
                maxWidth="max-content"
                customClassName="status-width"
              />

              <PillButtons
                placeholderText="Email Verified at"
                statusPlaceholder="statusActive"
                mainLabelText={userFilters.emailVerifiedAt.label}
                rightIcon={<RightIconOutlineRegularIcon />}
                onSelect={(item) => updateDateFilter("emailVerifiedAt", item)}
                menuItems={createdDateMenuItems}
                size="M"
                maxWidth="max-content"
                customClassName="status-width"
              />
            </div>

            <div className="search-dropdown-styles">
              <Select
                size="S"
                selectWrapperClassName="search-select"
                options={searchFieldOptions}
                value={userFilters.search_field}
                onChange={(option) =>
                  setUserFilters({ ...userFilters, search_field: option })
                }
                placeholder="Search by"
              />

              <Input
                id="searchUser"
                type="search"
                placeholder="Search in Table"
                leftIcon={<MagnifyingGlassOutlineRegularIcon />}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="S"
                className="search-input"
                rightIcon={<XOutlineRegularIcon />}
                handleClearSearch={() => setSearchTerm("")}
              />
            </div>
          </div>

          <CommonTable
            data={users}
            columns={tableColumns}
            total_count={pagination.total}
            loading={loading}
            initialLoading={loading}
            page={page}
            limit={pagination?.limit || 10}
            tableWrapperStyles="table-wrapper-styles"
            tableClassName="table-full-view"
            onPageChange={handlePageChange}
            sort_by={sortConfig.sort_by}
            sort_order={sortConfig.sort_order}
            onSortChange={handleSortChange}
          />
        </TableWrapperStyles>
      </CommonDashboardWrapper>
      <CustomDateModal
        show={showDateModal}
        showMonth={2}
        initialRange={customDateRange}
        onClose={() => setShowDateModal(false)}
        onApply={(range) => {
          setCustomDateRange(range);
          updateCustomDateRange(range);
        }}
      />
      <UserStatusModal
        show={showCompanyUsersModal}
        onClose={handleCloseCompanyUsersModal}
        companyId={companyUserStatus.companyId}
        status={companyUserStatus.status}
        loading={userStatusLoading}
        users={userStatus}
      />
    </ContainerWrapper>
  );
};

export default UsersManagement;
